import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, ChevronRight, Youtube, AlertTriangle,
  ShieldCheck, Users, Target, ArrowUpRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { SERVICES, getServiceBySlug } from '../data/services';
import { getVideosForTopics, CYBER_GUY_CHANNEL_URL } from '../data/videos';
import YouTubeVideoCard from '../components/YouTubeVideoCard';
import SEO, { SITE, orgRef, breadcrumb } from '../components/SEO';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const service = getServiceBySlug(slug);
  if (!service) return <Navigate to="/services" replace />;

  const videos = getVideosForTopics([service.name]).slice(0, 6);
  const otherServices = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white pt-16 sm:pt-20">
      <SEO
        path={`/services/${service.slug}`}
        title={`${service.name} | Kennyvicops Cyber Solutions Inc.`}
        description={service.description}
        image={service.image}
        jsonLd={[
          breadcrumb([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/services' },
            { name: service.name, path: `/services/${service.slug}` },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: service.name,
            serviceType: 'Cybersecurity Consulting',
            description: service.description,
            provider: orgRef,
            areaServed: 'Global',
            url: `${SITE}/services/${service.slug}`,
            audience: { '@type': 'BusinessAudience', audienceType: 'Small and growing businesses' },
            hasOfferCatalog: {
              '@type': 'OfferCatalog',
              name: `${service.name} — Key Capabilities`,
              itemListElement: service.areas.map((a) => ({
                '@type': 'Offer',
                itemOffered: { '@type': 'Service', name: a },
              })),
            },
          },
        ]}
      />

      {/* HERO — dark navy */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(22,135,255,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-60" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-24 pb-16 sm:pb-20">
          <div className="grid gap-10 lg:grid-cols-[1.15fr_1fr] lg:items-center">
            <div>
              <Link to="/services" className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 mb-4 inline-block hover:text-cyan-200 font-semibold">
                &larr; All Services
              </Link>
              <div className={`inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-gradient-to-br ${service.accent} shadow-lg mb-6`}>
                <service.icon className="w-6 h-6 text-white" />
              </div>
              <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-white">
                {service.name}
              </h1>
              <p className="mt-4 text-lg sm:text-xl text-cyan-100/90 font-medium">{service.headline}</p>
              <p className="mt-5 text-base sm:text-lg text-slate-300/95 leading-relaxed">
                {service.description}
              </p>
              <div className="mt-8">
                <Link to="/contact">
                  <Button
                    data-testid={`service-${service.slug}-hero-cta`}
                    className="px-7 py-6 bg-white text-[#06101F] hover:bg-cyan-100 rounded-full font-semibold"
                  >
                    Request a Security Consultation <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-cyan-400/20 via-blue-500/10 to-transparent rounded-[2rem] blur-2xl" aria-hidden />
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden ring-1 ring-white/10">
                <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#06101F]/50 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM — white */}
      <section className="relative bg-white bg-kv-grid-light text-[#101828]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
          <div className="grid gap-8 lg:grid-cols-[auto_1fr] lg:items-start">
            <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl w-fit">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <div className="inline-flex items-center gap-2.5 mb-4">
                <span className="w-6 h-px bg-blue-600/70" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold">The Business Problem</span>
              </div>
              <h2 className="font-editorial text-2xl sm:text-4xl font-semibold text-[#06101F] mb-5 leading-[1.1]">
                Why this matters for your business
              </h2>
              <p className="text-base sm:text-lg text-[#475467] leading-relaxed">
                {service.problem}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE DO — off-white */}
      <section className="relative bg-[#FAFBFD] bg-kv-grid-light text-[#101828] border-y border-slate-200/70">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
          <div className="grid gap-8 lg:grid-cols-[auto_1fr] lg:items-start">
            <div className="p-3.5 bg-blue-50 border border-blue-100 rounded-xl w-fit">
              <ShieldCheck className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="inline-flex items-center gap-2.5 mb-4">
                <span className="w-6 h-px bg-blue-600/70" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold">What We Do</span>
              </div>
              <h2 className="font-editorial text-2xl sm:text-4xl font-semibold text-[#06101F] mb-5 leading-[1.1]">
                How Kennyvicops helps
              </h2>
              <p className="text-base sm:text-lg text-[#475467] leading-relaxed">
                {service.whatWeDo}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* KEY CAPABILITIES — white */}
      <section className="relative bg-white bg-kv-grid-light text-[#101828]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
          <div className="max-w-2xl mb-10">
            <div className="inline-flex items-center gap-2.5 mb-4">
              <span className="w-6 h-px bg-blue-600/70" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold">Key Capabilities</span>
            </div>
            <h2 className="font-editorial text-2xl sm:text-4xl font-semibold text-[#06101F] leading-[1.1]">
              What this service typically covers
            </h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {service.areas.map((a) => (
              <div key={a} className="flex items-start gap-3 p-4 bg-[#F5F8FC] border border-slate-200 rounded-xl">
                <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm sm:text-[15px] text-[#101828] font-medium">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT HELPS — dark navy */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-kv-grid opacity-60" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_40%,rgba(22,135,255,0.14),transparent_55%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
          <div className="grid gap-8 lg:grid-cols-[auto_1fr] lg:items-start">
            <div className="p-3.5 bg-white/[0.06] border border-cyan-300/25 rounded-xl w-fit">
              <Users className="w-6 h-6 text-cyan-300" />
            </div>
            <div className="flex-1">
              <div className="inline-flex items-center gap-2.5 mb-4">
                <span className="w-6 h-px bg-cyan-300/70" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 font-semibold">Who It Helps</span>
              </div>
              <h2 className="font-editorial text-2xl sm:text-4xl font-semibold text-white mb-6 leading-[1.1]">
                Situations where this service adds real value
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {service.whoItHelps.map((w) => (
                  <div key={w} className="flex items-start gap-3 p-4 bg-white/[0.04] border border-white/10 rounded-xl">
                    <span className="w-2 h-2 rounded-full bg-cyan-400 mt-2 flex-shrink-0" />
                    <span className="text-sm sm:text-[15px] text-slate-200">{w}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED CYBER GUY CONTENT — dark navy (media) */}
      <section className="relative overflow-hidden bg-[#06101F] text-white border-t border-white/5">
        <div className="absolute inset-0 bg-kv-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <div className="inline-flex items-center gap-2.5 mb-4">
                <span className="w-6 h-px bg-cyan-300/70" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 font-semibold">Related Cyber Guy Content</span>
              </div>
              <h2 className="font-editorial text-2xl sm:text-4xl font-semibold text-white leading-[1.1]">
                Learn the concepts behind {service.name}
              </h2>
            </div>
            <a
              href={CYBER_GUY_CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-cyan-300 hover:text-cyan-200"
            >
              <Youtube className="w-4 h-4" /> Visit the channel
            </a>
          </div>
          {videos.length ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {videos.map((v) => (
                <YouTubeVideoCard key={v.youtubeId} video={v} showService={false} />
              ))}
            </div>
          ) : (
            <p className="text-slate-400 text-sm">More related videos coming soon.</p>
          )}
        </div>
      </section>

      {/* PRACTICAL OUTCOME — off-white */}
      <section className="relative bg-[#FAFBFD] bg-kv-grid-light text-[#101828] border-y border-slate-200/70">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
          <div className="grid gap-8 lg:grid-cols-[auto_1fr] lg:items-start">
            <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl w-fit">
              <Target className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <div className="inline-flex items-center gap-2.5 mb-4">
                <span className="w-6 h-px bg-blue-600/70" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold">Practical Outcome</span>
              </div>
              <h2 className="font-editorial text-2xl sm:text-4xl font-semibold text-[#06101F] mb-5 leading-[1.1]">
                What your business should walk away with
              </h2>
              <p className="text-base sm:text-lg text-[#475467] leading-relaxed">
                {service.outcome}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA — dark navy */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(22,135,255,0.22),transparent_50%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-70" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-24 text-center">
          <h2 className="font-editorial text-3xl sm:text-5xl font-semibold text-white leading-[1.1]">
            Ready to talk about {service.name.toLowerCase()} for your business?
          </h2>
          <p className="mt-5 text-slate-300/95 text-base sm:text-lg max-w-2xl mx-auto">
            Every business is different. Let&apos;s discuss what makes sense in your environment.
          </p>
          <div className="mt-8">
            <Link to="/contact">
              <Button
                data-testid={`service-${service.slug}-cta`}
                className="px-8 py-6 bg-white text-[#06101F] hover:bg-cyan-100 rounded-full font-semibold"
              >
                Request a Security Consultation <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* OTHER SERVICES — white */}
      <section className="relative bg-white bg-kv-grid-light text-[#101828]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
          <div className="inline-flex items-center gap-2.5 mb-5">
            <span className="w-6 h-px bg-blue-600/70" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold">Explore Other Services</span>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {otherServices.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group bg-white border border-slate-200 hover:border-blue-500/50 hover:shadow-lg hover:shadow-slate-900/5 hover:-translate-y-0.5 rounded-2xl p-6 transition-all"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className={`inline-flex w-10 h-10 items-center justify-center rounded-xl bg-gradient-to-br ${s.accent} shadow-md`}>
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
                </div>
                <h3 className="font-editorial text-lg font-semibold text-[#06101F] mb-2">{s.name}</h3>
                <p className="text-sm text-[#475467] leading-relaxed mb-4">{s.short}</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-600">
                  Learn more <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
